import React from 'react';
import { Footer as FooterOrganism } from '@barges/react';
import { analyticsService } from '@common/analytics';
import { ANALYTICS_EVENTS, CUSTOMER_SUPPORT_EVENTS } from '@common/analytics/AnalyticsEvents';
import classes from './classes.module.scss';
import {
  appLinks, socialLinks, footerCopyright
} from '../../../config/const';
import {
  downloadAppLink, fromMobileApp, isKA, isPGW
} from '../../../../common/config/utils';
import '@barges/scss/lib/title.css';
import '@barges/scss/lib/footer.css';
import { appRoutes, dynamicRoutes, getSpecialitiesRoutes } from '../../../config/app_routes';
import { getCurrentLocalization, DEFAULT_LANGUAGE } from '../../../config/i18n';
import { SvgIcon } from '../../../../common/sprites';
import { useLocalizedPath } from '../../routes/hooks/useLocalizedPath';
import { footerLogo } from '../../../logo/logo';

/** AppsFlyer channel shared by both footer badges. */
const FOOTER_AF_CHANEL = 'seo';

/** Per-store campaigns, so the two badges stay separable in AppsFlyer. */
const CAMPAIGN_APP_STORE = 'webappstore_footer';
const CAMPAIGN_PLAY_STORE = 'webplaystore_footer';

const socialLinksArray = [{
  href: socialLinks.x,
  key: 1,
  icon: <SvgIcon id="twitter_footer" size={ 28 } />
}, {
  href: socialLinks.fb,
  key: 2,
  icon: <SvgIcon id="fb_footer" size={ 28 } />
}, {
  href: socialLinks.ig,
  key: 3,
  icon: <SvgIcon id="ig_footer" size={ 28 } />
}, {
  href: socialLinks.tt,
  key: 4,
  icon: <SvgIcon id="tiktok_footer" size={ 28 } />
}, {
  href: socialLinks.yt,
  key: 5,
  icon: <SvgIcon id="yt_footer" size={ 28 } />
}];

const onIconTap = () => {
  window.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
};

const companyInfo = `${ new Date().getFullYear() } `;

const language = getCurrentLocalization();

const logoSrc = () => <img alt="logo" src={ footerLogo() } />;

const Footer = ({
  t, appStoreUrl, playStoreUrl, satisfactionGuarantee
}) => {
  const currentLang = getCurrentLocalization();
  const generatePath = useLocalizedPath();
  if (fromMobileApp) return null;

  const specialities = getSpecialitiesRoutes()
    .map(key => ({ name: (dynamicRoutes[key].translations?.[currentLang].name || dynamicRoutes[key].name), key }));

  const appStoreLink = downloadAppLink({ afChanel: FOOTER_AF_CHANEL, url: appStoreUrl, campaign: CAMPAIGN_APP_STORE });
  const playStoreLink = downloadAppLink({ afChanel: FOOTER_AF_CHANEL, url: playStoreUrl, campaign: CAMPAIGN_PLAY_STORE });

  // Only report a download once the badge can actually navigate — a badge without
  // an href is still clickable, and reporting it would inflate `download app` with
  // clicks that never reached a store.
  const handleDownloadAppClicked = (store, href) => {
    if (!href) return;
    analyticsService.trackEvent(ANALYTICS_EVENTS.DOWNLOAD_APP, { store });
  };

  return (
    <div className={ classes.footerHolder } data-testid="mainFooterID">
      <div className={ classes.footerContent }>
        <FooterOrganism
          companyInfo={ `© ${ companyInfo }${ footerCopyright }` }
          logo={ logoSrc() }
          termsLinks={ [
            { name: t('privacy_policy'), path: appLinks.privacyPolicy },
            { name: t('terms_of_use'), path: appLinks.tos },
            { name: t('affiliation'), path: appLinks.affiliation }
          ] }
          logoAction={ onIconTap }
          socialLinks={ socialLinksArray }
          downloadAppSection={ {
            title: t('footerLinks.download_app_title'),
            links: [
              <a key="app_store_badge" href={ appStoreLink } onClick={ () => handleDownloadAppClicked('app store', appStoreLink) }>
                <SvgIcon size={ [132, 44] } id="app_store_badge" />
              </a>,
              <a key="google_play_badge" href={ playStoreLink } onClick={ () => handleDownloadAppClicked('google play', playStoreLink) }>
                <SvgIcon size={ [149, 44] } id="google_play_badge" />
              </a>
            ]
          } }
          links={ [
            {
              title: t('footerLinks.explore'),
              children: [
                { name: t('footerLinks.advisors'), path: generatePath(currentLang, appRoutes.psychics) },
                ...isKA()
                  ? [
                    { name: t('footerLinks.specialities'), path: generatePath(currentLang, appRoutes.specialities) },
                    { name: t('footerLinks.horoscope'), path: appLinks.myHoroscope }
                  ]
                  : [],
                ...isPGW()
                  ? [{ name: t('footerLinks.our_blog'), path: language === DEFAULT_LANGUAGE ? appLinks.blog : `${ appLinks.blog }/${ language }` }]
                  : [{ name: t('footerLinks.articles'), path: appLinks.articles }]
              ]
            },
            {
              title: t('footerLinks.specialities'),
              children: specialities?.map(({ name, key }) => ({ name, path: generatePath(currentLang, key) })) || []
            },
            {
              title: t('footerLinks.more'),
              children: [
                { name: t('footerLinks.apply_promo_code'), path: generatePath(currentLang, appRoutes.promoCode) },
                {
                  name: t('footerLinks.customer_support'),
                  onClick: () => {
                    analyticsService.trackEvent(CUSTOMER_SUPPORT_EVENTS.SUPPORT_CLICKED, { 'click source': 'footer' });
                    window.open(appLinks.customerSupport, '_blank', 'noopener,noreferrer');
                  }
                },
                ...(satisfactionGuarantee ? [{ name: satisfactionGuarantee.screenTitle, path: satisfactionGuarantee.link }] : []),
                { name: t('footerLinks.about_us'), path: appLinks.aboutUs },
                { name: t('footerLinks.advisor_verify'), path: appLinks.advisorVerify },
                { name: t('footerLinks.psychic_apply_here'), path: appLinks.forAdvisors }
              ]
            }
          ] }
        />
      </div>
      {/* Session ID show in right bottom corner to relate logs to specific session */}
      <div style={ {
        position: 'absolute', bottom: 4, right: 4, fontSize: 10, color: '#999', pointerEvents: 'none'
      } }
      >
        { window?.sessionId }
      </div>
    </div>
  );
};

export default Footer;
